/**
 * Inventory item-type behavior — pure logic (PRD.266).
 *
 * A single source of truth for how each inventory item type behaves across the
 * app: which fields are locked, whether it is stock-tracked, whether it can be
 * received in a Delivery Order, appears in the Stock Card / billing / used
 * supplies pickers, and which future clinical-history hooks it feeds.
 *
 * Item types are stored lowercase in the DB. Adding a new type should mean
 * adding one entry to `ITEM_TYPE_BEHAVIOR` rather than sprinkling
 * `item_type === 'x'` checks across the codebase.
 *
 * Import-free so it can be unit-tested with Node's built-in runner and reused
 * by both the web-admin UI and data-access queries.
 */

export type ItemTypeKey =
  | 'medicine'
  | 'product'
  | 'supply'
  | 'service'
  | 'package'
  | 'vaccination'
  | 'injection';

/** How a boolean item field is governed for a given item type. */
export type FieldLock =
  | 'locked-true' // always true, not user-editable
  | 'locked-false' // always false, not user-editable
  | 'default-true' // defaults true, user may change
  | 'default-false' // defaults false, user may change
  | 'configurable'; // user-chosen, no strong default

export interface ItemTypeBehavior {
  /** Lowercase DB value. */
  key: ItemTypeKey;
  /** Singular user-facing label. */
  label: string;
  /** Plural user-facing label. */
  labelPlural: string;
  /** Billable/sellable governance. */
  sellable: FieldLock;
  /** Stock tracking governance. */
  trackStock: FieldLock;
  /** Batch/lot/expiry governance. */
  batchTracked: FieldLock;
  /** Receivable through Delivery Orders / Direct Delivery Orders. */
  receivableInDeliveryOrder: boolean;
  /** Eligible to appear in the Stock Card ledger. */
  appearsInStockCard: boolean;
  /** Eligible for the billing/invoice item picker (subject to is_sellable). */
  appearsInBillingPicker: boolean;
  /** Eligible for the Used Supplies picker. */
  appearsInUsedSupplies: boolean;
  /** Can be bundled into a package (subject to is_sellable). */
  bundleableInPackage: boolean;
  /** Deducts stock when billed/administered/redeemed (fulfillment). */
  fulfillmentDeductsStock: boolean;
  /**
   * Routes to an invoice-driven stock-out flow — Pharmacy Confirm Dispense or
   * Product Release — so its invoice lines show a "Ready for Pharmacy/Release"
   * fulfillment badge and task. True for medicine/product/vaccination. Supplies
   * deduct stock through Used Supplies (not the invoice), and injection is
   * non-stock, so neither shows a fulfillment badge (PRD.266).
   */
  routesToInvoiceFulfillment: boolean;
  /** May contribute to a future immunization history record. */
  immunizationHistory: boolean;
  /** May contribute to a future medication-administration history record. */
  medicationAdminHistory: boolean;
}

/**
 * Behavior table. PRD.266 adds `vaccination` and `injection`; the pre-existing
 * types are described here too so eligibility checks have one home.
 */
export const ITEM_TYPE_BEHAVIOR: Record<ItemTypeKey, ItemTypeBehavior> = {
  medicine: {
    key: 'medicine',
    label: 'Medicine',
    labelPlural: 'Medicines',
    sellable: 'locked-true',
    trackStock: 'default-true',
    batchTracked: 'default-true',
    receivableInDeliveryOrder: true,
    appearsInStockCard: true,
    appearsInBillingPicker: true,
    appearsInUsedSupplies: false,
    bundleableInPackage: true,
    fulfillmentDeductsStock: true,
    routesToInvoiceFulfillment: true,
    immunizationHistory: false,
    medicationAdminHistory: false,
  },
  product: {
    key: 'product',
    label: 'Product',
    labelPlural: 'Products',
    sellable: 'locked-true',
    trackStock: 'default-true',
    batchTracked: 'default-false',
    receivableInDeliveryOrder: true,
    appearsInStockCard: true,
    appearsInBillingPicker: true,
    appearsInUsedSupplies: false,
    bundleableInPackage: true,
    fulfillmentDeductsStock: true,
    routesToInvoiceFulfillment: true,
    immunizationHistory: false,
    medicationAdminHistory: false,
  },
  supply: {
    key: 'supply',
    label: 'Supply',
    labelPlural: 'Supplies',
    sellable: 'configurable',
    trackStock: 'default-true',
    batchTracked: 'configurable',
    receivableInDeliveryOrder: true,
    appearsInStockCard: true,
    appearsInBillingPicker: true,
    appearsInUsedSupplies: true,
    bundleableInPackage: true,
    fulfillmentDeductsStock: true,
    routesToInvoiceFulfillment: false,
    immunizationHistory: false,
    medicationAdminHistory: false,
  },
  service: {
    key: 'service',
    label: 'Service',
    labelPlural: 'Services',
    sellable: 'locked-true',
    trackStock: 'locked-false',
    batchTracked: 'locked-false',
    receivableInDeliveryOrder: false,
    appearsInStockCard: false,
    appearsInBillingPicker: true,
    appearsInUsedSupplies: false,
    bundleableInPackage: true,
    fulfillmentDeductsStock: false,
    routesToInvoiceFulfillment: false,
    immunizationHistory: false,
    medicationAdminHistory: false,
  },
  package: {
    key: 'package',
    label: 'Package',
    labelPlural: 'Packages',
    sellable: 'locked-true',
    trackStock: 'locked-false',
    batchTracked: 'locked-false',
    receivableInDeliveryOrder: false,
    appearsInStockCard: false,
    appearsInBillingPicker: true,
    appearsInUsedSupplies: false,
    // A package cannot be nested inside another package.
    bundleableInPackage: false,
    fulfillmentDeductsStock: false,
    routesToInvoiceFulfillment: false,
    immunizationHistory: false,
    medicationAdminHistory: false,
  },
  // PRD.266 — stock-tracked vaccine product.
  vaccination: {
    key: 'vaccination',
    label: 'Vaccination',
    labelPlural: 'Vaccinations',
    sellable: 'locked-true',
    trackStock: 'locked-true',
    batchTracked: 'default-true',
    receivableInDeliveryOrder: true,
    appearsInStockCard: true,
    appearsInBillingPicker: true,
    appearsInUsedSupplies: false,
    bundleableInPackage: true,
    fulfillmentDeductsStock: true,
    routesToInvoiceFulfillment: true,
    immunizationHistory: true,
    medicationAdminHistory: false,
  },
  // PRD.266 — non-stock administered-medication item.
  injection: {
    key: 'injection',
    label: 'Injection',
    labelPlural: 'Injections',
    sellable: 'locked-true',
    trackStock: 'locked-false',
    batchTracked: 'locked-false',
    receivableInDeliveryOrder: false,
    appearsInStockCard: false,
    appearsInBillingPicker: true,
    appearsInUsedSupplies: false,
    bundleableInPackage: true,
    fulfillmentDeductsStock: false,
    routesToInvoiceFulfillment: false,
    immunizationHistory: false,
    medicationAdminHistory: true,
  },
};

/** Normalize any casing / unknown value to a known key, or null. */
export function toItemTypeKey(itemType: string | null | undefined): ItemTypeKey | null {
  const t = (itemType ?? '').toLowerCase();
  return t in ITEM_TYPE_BEHAVIOR ? (t as ItemTypeKey) : null;
}

/** Behavior for an item type; falls back to `medicine` for unknown/legacy. */
export function getItemTypeBehavior(itemType: string | null | undefined): ItemTypeBehavior {
  return ITEM_TYPE_BEHAVIOR[toItemTypeKey(itemType) ?? 'medicine'];
}

// ─── Field-lock helpers ───

/** True when `is_sellable` cannot be edited (always the locked value). */
export function shouldLockSellable(itemType: string | null | undefined): boolean {
  const l = getItemTypeBehavior(itemType).sellable;
  return l === 'locked-true' || l === 'locked-false';
}

/** True when `track_stock` cannot be edited. */
export function shouldLockTrackStock(itemType: string | null | undefined): boolean {
  const l = getItemTypeBehavior(itemType).trackStock;
  return l === 'locked-true' || l === 'locked-false';
}

/** Whether batch tracking may be toggled by the user for this item type. */
export function shouldAllowBatchTracking(itemType: string | null | undefined): boolean {
  const l = getItemTypeBehavior(itemType).batchTracked;
  return l !== 'locked-true' && l !== 'locked-false';
}

/** Resolve the locked/default boolean value for a `FieldLock`. */
export function fieldLockValue(lock: FieldLock): boolean {
  return lock === 'locked-true' || lock === 'default-true';
}

/** The enforced/default `is_sellable` for an item type (0/1-friendly). */
export function defaultSellable(itemType: string | null | undefined): boolean {
  return fieldLockValue(getItemTypeBehavior(itemType).sellable);
}

/** The enforced/default `track_stock` for an item type. */
export function defaultTrackStock(itemType: string | null | undefined): boolean {
  return fieldLockValue(getItemTypeBehavior(itemType).trackStock);
}

/** The default `batch_tracked` for an item type. */
export function defaultBatchTracked(itemType: string | null | undefined): boolean {
  return fieldLockValue(getItemTypeBehavior(itemType).batchTracked);
}

/**
 * Effective batch-tracking for an item row.
 *
 * Medicine and Product are expiry/lot-bearing and have no batch-tracked toggle
 * in the item form, so they are ALWAYS batch-tracked — regardless of the stored
 * flag. `batch_tracked` was added after these types existed, so legacy rows
 * carry either null (predating the column) or an unintended 0 (the old form
 * forced 0 for non-supply types); neither should suppress the batch/expiry
 * prompt on delivery-order receipt, direct receipt or inventory adjustment.
 *
 * Supply and Vaccination expose a real toggle, so they follow the stored flag.
 *
 * Pass the raw stored flag (0/1, boolean, or null) and the row's `item_type`.
 */
export function resolveBatchTracked(
  batchTracked: number | boolean | null | undefined,
  itemType: string | null | undefined,
): boolean {
  const key = toItemTypeKey(itemType);
  if (key === 'medicine' || key === 'product') return true;
  return batchTracked === 1 || batchTracked === true;
}

// ─── Eligibility helpers (accept an item type or an item-like row) ───

type ItemLike = { item_type?: string | null; track_stock?: number | boolean | null };

function typeOf(input: string | null | undefined | ItemLike): string | null | undefined {
  return typeof input === 'object' && input !== null ? input.item_type : input;
}

export function canReceiveInDeliveryOrder(input: string | null | undefined | ItemLike): boolean {
  return getItemTypeBehavior(typeOf(input)).receivableInDeliveryOrder;
}

/**
 * Stock Card eligibility. A vaccination/medicine/product/supply is eligible
 * when stock-tracked; injection/service/package never are. When passed a row,
 * a falsy `track_stock` also excludes it (e.g. a non-stock supply).
 */
export function canAppearInStockCard(input: string | null | undefined | ItemLike): boolean {
  const behavior = getItemTypeBehavior(typeOf(input));
  if (!behavior.appearsInStockCard) return false;
  if (typeof input === 'object' && input !== null && input.track_stock != null) {
    return input.track_stock === 1 || input.track_stock === true;
  }
  return true;
}

export function canAppearInBillingPicker(input: string | null | undefined | ItemLike): boolean {
  return getItemTypeBehavior(typeOf(input)).appearsInBillingPicker;
}

export function canAppearInUsedSupplies(input: string | null | undefined | ItemLike): boolean {
  return getItemTypeBehavior(typeOf(input)).appearsInUsedSupplies;
}

export function canBundleInPackage(input: string | null | undefined | ItemLike): boolean {
  return getItemTypeBehavior(typeOf(input)).bundleableInPackage;
}

/** Whether billing/redemption/administration should deduct stock. */
export function fulfillmentDeductsStock(input: string | null | undefined | ItemLike): boolean {
  return getItemTypeBehavior(typeOf(input)).fulfillmentDeductsStock;
}

/**
 * Whether an invoice line of this item type routes to an invoice-driven
 * stock-out (Pharmacy dispense / Product Release) and therefore shows a
 * "Ready for Pharmacy/Release" fulfillment badge. Unknown/legacy types are
 * NOT assumed to fulfill (returns false) so a non-fulfilling line never shows
 * a stray badge before its type resolves.
 */
export function routesToInvoiceFulfillment(input: string | null | undefined | ItemLike): boolean {
  const key = toItemTypeKey(typeOf(input));
  return key ? ITEM_TYPE_BEHAVIOR[key].routesToInvoiceFulfillment : false;
}

export function canCreateImmunizationHistory(input: string | null | undefined | ItemLike): boolean {
  return getItemTypeBehavior(typeOf(input)).immunizationHistory;
}

export function canCreateMedicationAdministrationHistory(
  input: string | null | undefined | ItemLike,
): boolean {
  return getItemTypeBehavior(typeOf(input)).medicationAdminHistory;
}

// ─── Canonical item-type lists for queries/pickers ───

/** Physical, receivable item types for Delivery Orders (PRD.265/266). */
export const RECEIVABLE_ITEM_TYPES: readonly ItemTypeKey[] = (
  Object.keys(ITEM_TYPE_BEHAVIOR) as ItemTypeKey[]
).filter((k) => ITEM_TYPE_BEHAVIOR[k].receivableInDeliveryOrder);

/** Item types offered in the billing/invoice picker (subject to is_sellable). */
export const BILLABLE_ITEM_TYPES: readonly ItemTypeKey[] = (
  Object.keys(ITEM_TYPE_BEHAVIOR) as ItemTypeKey[]
).filter((k) => ITEM_TYPE_BEHAVIOR[k].appearsInBillingPicker);
